/**
 * Generates meaningful context for Gemini suggestion API when no context is provided.
 * Uses the field label (and optional user input) to describe what kind of suggestions are needed.
 */

const LABEL_CONTEXT_MAP = {
  'organization/employer/institute': (input) =>
    input
      ? `User is entering their organization, employer, or institute. Current input: "${input}". Suggest registered organizations, well-known companies, universities, or institutes that match this name or are related.`
      : 'User is entering their current company, university, or institute. Suggest well-known organizations, employers, or educational institutions.',
  'job title / major': (input) =>
    input
      ? `User is entering their professional job title or academic major. Current input: "${input}". Suggest common job titles, roles, or fields of study that match or complete this.`
      : 'User is entering their professional title or field of study. Suggest common job titles, roles, or academic majors.',
  'motivation statement': (input) =>
    input
      ? `User is writing a short motivation statement (e.g., why they want to support student innovation). Current input: "${input}". Suggest concise phrases or key points that could complete or improve this statement (max 150 characters).`
      : 'User is writing why they want to support student innovation. Suggest concise motivation phrases or key points (150 characters max).',
  'institution name (college/university)': (input) =>
    input
      ? `User is entering their college or university name. Current input: "${input}". Suggest accredited institutions, universities, or colleges that match this name.`
      : 'User is entering their college or university. Suggest accredited institutions, universities, or colleges.',
  'primary stream': (input) =>
    input
      ? `User is selecting their primary academic or career stream. Current input: "${input}". Suggest common streams (e.g., Engineering, Computer Science, Arts, Business) that match.`
      : 'User is selecting their primary stream. Suggest common academic or career streams like Engineering, Science, Arts, Commerce, etc.',
  specialization: (input) =>
    input
      ? `User is entering their specialization within their field. Current input: "${input}". Suggest relevant specializations or focus areas that match.`
      : 'User is entering their specialization. Suggest relevant specializations or focus areas within their field of study or work.',
  'website url': (input) =>
    input
      ? `User is entering their company or organization website. Current input: "${input}". Suggest plausible official website URLs (use https://)—full URLs or recognizable domains that match companies or partial names the user typed.`
      : 'User is entering their organization website. Suggest example website URLs (https://) for typical company or sponsor sites.',
  'corporate portal': (input) =>
    input
      ? `User is entering their corporate portal URL (company or organization). Current input: "${input}". Suggest plausible official portal or website URLs (use https://)—full URLs or recognizable domains that match companies or partial names the user typed.`
      : 'User is entering their corporate portal URL. Suggest example URLs (https://) for typical company, investor, or sponsor portal sites.',
};

/**
 * Get context string for a Gemini field based on its label and optional user input.
 * Used when the front end does not provide context so the backend gets meaningful hints.
 *
 * @param {string} label - The field label (e.g. "Organization/Employer/Institute")
 * @param {string} [input=''] - Current user input (e.g. "neural limb")
 * @returns {string} Context string to send to the Gemini suggestions API
 */
export function getContextForGeminiLabel(label, input = '') {
  if (!label || typeof label !== 'string') return '';
  const normalizedLabel = label.trim().toLowerCase();
  const generator = LABEL_CONTEXT_MAP[normalizedLabel];
  const trimmedInput = typeof input === 'string' ? input.trim() : '';
  if (generator) return generator(trimmedInput);

  const displayLabel = label.trim();
  // Fallback for any other Gemini field: generic context from label + input
  if (trimmedInput) {
    return `Field: ${displayLabel}. User input: "${trimmedInput}". Suggest relevant options or completions for this field.`;
  }
  return `Field: ${displayLabel}. Suggest relevant options or completions for this field.`;
}
